import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Play, Maximize2 } from 'lucide-react';
import styled from 'styled-components';

// Styled Components
const Section = styled.section`
  padding: 0;
  width: 100%;
  overflow: hidden;
`;

const Container = styled.div`
  width: 100%;
  max-width: none;
  margin: 0;
`;

const Header = styled.div`
  text-align: center;
  padding: 3rem 1rem 2rem;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  
  @media (max-width: 768px) {
    padding: 2rem 1rem 1.5rem;
  }
  
  h2 {
    font-size: clamp(1.5rem, 4vw, 2.5rem);
    font-weight: 700;
    color: #1f2937;
    margin-bottom: 0.5rem;
  }
  
  p {
    font-size: clamp(0.9rem, 2vw, 1.1rem);
    color: #6b7280;
    max-width: 48rem;
    margin: 0 auto;
  }
`;

const CarouselContainer = styled.div`
  position: relative;
  width: 100%;
  margin: 0;
  background: white;
  overflow: hidden;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15);
`;

const CarouselViewport = styled.div`
  position: relative;
  width: 100%;
  height: 70vh;
  min-height: 500px;
  max-height: 800px;
  overflow: hidden;
  
  @media (max-width: 768px) {
    height: 60vh;
    min-height: 400px;
    max-height: 600px;
  }
`;

const CarouselSlide = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(0, 0, 0, 0.7) 0%,
      rgba(0, 0, 0, 0.3) 50%,
      rgba(0, 0, 0, 0.8) 100%
    );
    z-index: 1;
  }
`;

const PhoneImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: scale(1.02);
  }
`;

const NavigationButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 3rem;
  height: 3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 10;
  
  &:hover {
    background: white;
    transform: translateY(-50%) scale(1.1);
  }
  
  &.prev {
    left: 1rem;
  }
  
  &.next {
    right: 1rem;
  }
  
  @media (max-width: 768px) {
    width: 2.5rem;
    height: 2.5rem;
    
    &.prev {
      left: 0.5rem;
    }
    
    &.next {
      right: 0.5rem;
    }
  }
`;

const DotsContainer = styled.div`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 0.75rem;
  z-index: 10;
`;

const Dot = styled.button`
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition: all 0.3s ease;
  
  &.active {
    background: white;
    transform: scale(1.2);
  }
  
  &:hover {
    background: rgba(255, 255, 255, 0.8);
  }
`;

const PhoneInfo = styled.div`
  position: absolute;
  bottom: 3rem;
  left: 3rem;
  color: white;
  z-index: 10;
  text-shadow: 
    0 2px 8px rgba(0, 0, 0, 0.9),
    0 0 20px rgba(0, 0, 0, 0.5);
  background: rgba(0, 0, 0, 0.3);
  padding: 1.5rem 2rem;
  border-radius: 1rem;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  max-width: 500px;
  
  @media (max-width: 768px) {
    left: 1.5rem;
    bottom: 2rem;
    padding: 1rem 1.5rem;
    max-width: 90%;
  }
  
  h3 {
    font-size: clamp(1.8rem, 4vw, 3rem);
    font-weight: 800;
    margin-bottom: 0.5rem;
    letter-spacing: -0.02em;
  }
  
  p {
    font-size: clamp(1.1rem, 2.5vw, 1.4rem);
    opacity: 1;
    font-weight: 500;
    line-height: 1.3;
  }
`;

const PhoneCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const phones = [
    {
      id: 1,
      name: 'iPhone 15 Pro Max',
      description: 'Le plus puissant jamais créé',
      image: 'https://i.pinimg.com/1200x/5a/09/cf/5a09cf9b844b75dcc75454f545b713db.jpg',
      color: '#1a1a2e'
    },
    {
      id: 2,
      name: 'iPhone 17 Pro',
      description: 'Performance professionnelle',
      image: 'https://i.pinimg.com/1200x/6c/3a/f4/6c3af4f16d30c4d06e72ceaa74b42fa3.jpg',
      color: '#2d3748'
    },
    {
      id: 3,
      name: 'iPhone 15 Plus',
      description: 'Écran grand format',
      image: 'https://i.pinimg.com/736x/66/4c/11/664c112582924416c7bfb42da8a3a2e9.jpg',
      color: '#4a5568'
    },
    {
      id: 4,
      name: 'iPhone 15',
      description: 'Le parfait équilibre',
      image: 'https://i.pinimg.com/1200x/c1/d3/f2/c1d3f27cc64fb7fd12adbbb04c5bcddc.jpg',
      color: '#718096'
    }
  ];

  const nextSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % phones.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + phones.length) % phones.length);
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  // Auto-play functionality
  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      nextSlide();
    }, 2500);

    return () => clearInterval(interval);
  }, [isAutoPlaying, currentIndex]);

  // Pause auto-play on hover
  const handleMouseEnter = () => setIsAutoPlaying(false);
  const handleMouseLeave = () => setIsAutoPlaying(true);

  return (
    <Section id="phone-carousel">
      <Container>
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <Header>
            <h2>Découvrez la gamme iPhone</h2>
            <p>
              Explorez notre collection complète d'iPhone, du modèle Pro au modèle standard.
            </p>
          </Header>
        </motion.div>

        {/* Carousel */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <CarouselContainer
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <CarouselViewport>
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentIndex}
                  initial={{ opacity: 0, x: 300 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -300 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                >
                  <CarouselSlide style={{ background: `linear-gradient(135deg, ${phones[currentIndex].color} 0%, #0f172a 100%)` }}>
                    <PhoneImage
                      src={phones[currentIndex].image}
                      alt={phones[currentIndex].name}
                    />
                    <PhoneInfo>
                      <h3>{phones[currentIndex].name}</h3>
                      <p>{phones[currentIndex].description}</p>
                    </PhoneInfo>
                  </CarouselSlide>
                </motion.div>
              </AnimatePresence>

              {/* Navigation Buttons */}
              <NavigationButton className="prev" onClick={prevSlide}>
                <ChevronLeft size={20} />
              </NavigationButton>
              <NavigationButton className="next" onClick={nextSlide}>
                <ChevronRight size={20} />
              </NavigationButton>

              {/* Dots Navigation */}
              <DotsContainer>
                {phones.map((_, index) => (
                  <Dot
                    key={index}
                    className={index === currentIndex ? 'active' : ''}
                    onClick={() => goToSlide(index)}
                  />
                ))}
              </DotsContainer>
            </CarouselViewport>
          </CarouselContainer>
        </motion.div>
      </Container>
    </Section>
  );
};

export default PhoneCarousel;
